import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layouts/AdminLayout';
import {
  Search,
  Filter,
  Plus,
  MoreVertical,
  Phone,
  Mail,
  MessageSquare,
  User,
  Building,
  Calendar,
  Star,
  ChevronRight,
  X
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

const LeadsPage = () => {
  const { api } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const stages = [
    { id: 'new', label: 'New', color: 'bg-blue-500' },
    { id: 'contacted', label: 'Contacted', color: 'bg-yellow-500' },
    { id: 'proposal_sent', label: 'Proposal Sent', color: 'bg-purple-500' },
    { id: 'negotiation', label: 'Negotiation', color: 'bg-orange-500' },
    { id: 'won', label: 'Won', color: 'bg-green-500' },
    { id: 'lost', label: 'Lost', color: 'bg-red-500' },
  ];

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await api.get('/leads');
      setLeads(response.data);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await api.patch(`/leads/${leadId}`, { status: newStatus });
      setLeads(leads.map(lead => 
        lead.id === leadId ? { ...lead, status: newStatus } : lead
      ));
    } catch (error) {
      console.error('Failed to update lead:', error);
    }
  };

  const convertToClient = async (leadId) => {
    try {
      await api.post(`/leads/${leadId}/convert`);
      fetchLeads();
      setDetailOpen(false);
    } catch (error) {
      console.error('Failed to convert lead:', error);
    }
  };

  const getLeadsByStage = (stageId) => {
    return leads.filter(lead => lead.status === stageId);
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-green-400';
    if (score >= 40) return 'text-yellow-400';
    return 'text-red-400';
  };

  const filteredLeads = leads.filter(lead =>
    lead.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lead.company?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lead.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDragStart = (e, leadId) => {
    e.dataTransfer.setData('leadId', leadId);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, stageId) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData('leadId');
    updateLeadStatus(leadId, stageId);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-syne font-bold text-white">Leads Pipeline</h1>
            <p className="text-fp-textMuted text-sm mt-1">{leads.length} total leads</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fp-textMuted" />
              <Input
                placeholder="Search leads..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-fp-surface border-white/10 text-white w-64"
                data-testid="leads-search"
              />
            </div>
          </div>
        </div>

        {/* Kanban Board */}
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-4 min-w-max">
            {stages.map((stage) => (
              <div
                key={stage.id}
                className="kanban-column"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage.id)}
              >
                {/* Column Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${stage.color}`} />
                    <h3 className="font-semibold text-white">{stage.label}</h3>
                    <span className="text-fp-textMuted text-sm">
                      ({getLeadsByStage(stage.id).length})
                    </span>
                  </div>
                </div>

                {/* Column Content */}
                <div className="space-y-3 min-h-[200px]">
                  {getLeadsByStage(stage.id)
                    .filter(lead => 
                      lead.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      lead.company?.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    .map((lead) => (
                    <motion.div
                      key={lead.id}
                      className="kanban-card"
                      draggable
                      onDragStart={(e) => handleDragStart(e, lead.id)}
                      onClick={() => {
                        setSelectedLead(lead);
                        setDetailOpen(true);
                      }}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      data-testid={`lead-card-${lead.id}`}
                    >
                      {/* Score Badge */}
                      <div className="flex items-center justify-between mb-3">
                        <span className={`font-mono text-sm ${getScoreColor(lead.score)}`}>
                          Score: {lead.score}
                        </span>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-1 hover:bg-white/10 rounded">
                              <MoreVertical className="w-4 h-4 text-fp-textMuted" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="bg-fp-surface border-white/10">
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(`https://wa.me/${lead.phone.replace(/\D/g, '')}`, '_blank');
                              }}
                            >
                              <MessageSquare className="w-4 h-4 mr-2" />
                              WhatsApp
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(`mailto:${lead.email}`, '_blank');
                              }}
                            >
                              <Mail className="w-4 h-4 mr-2" />
                              Email
                            </DropdownMenuItem>
                            {stage.id !== 'won' && (
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  convertToClient(lead.id);
                                }}
                              >
                                <User className="w-4 h-4 mr-2" />
                                Convert to Client
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      {/* Lead Info */}
                      <h4 className="font-semibold text-white mb-1">{lead.full_name}</h4>
                      {lead.company && (
                        <p className="text-fp-textMuted text-sm flex items-center gap-1 mb-2">
                          <Building className="w-3 h-3" />
                          {lead.company}
                        </p>
                      )}

                      {/* Services */}
                      {lead.services_needed?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {lead.services_needed.slice(0, 2).map((service) => (
                            <span
                              key={service}
                              className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded"
                            >
                              {service.replace('_', ' ')}
                            </span>
                          ))}
                          {lead.services_needed.length > 2 && (
                            <span className="px-2 py-0.5 bg-white/5 text-fp-textMuted text-xs rounded">
                              +{lead.services_needed.length - 2}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Timeline Badge */}
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5">
                        <Calendar className="w-3 h-3 text-fp-textMuted" />
                        <span className="text-fp-textMuted text-xs capitalize">
                          {lead.timeline?.replace('_', ' ') || 'No timeline'}
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Lead Detail Modal */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="bg-fp-surface border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white font-syne">Lead Details</DialogTitle>
            </DialogHeader>
            {selectedLead && (
              <div className="space-y-6">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-white">{selectedLead.full_name}</h2>
                    {selectedLead.company && (
                      <p className="text-fp-textMuted">{selectedLead.company}</p>
                    )}
                  </div>
                  <span className={`font-mono text-lg ${getScoreColor(selectedLead.score)}`}>
                    Score: {selectedLead.score}
                  </span>
                </div>

                {/* Contact Actions */}
                <div className="flex gap-3">
                  <a
                    href={`https://wa.me/${selectedLead.phone.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                    data-testid="lead-whatsapp-btn"
                  >
                    <MessageSquare className="w-4 h-4" />
                    WhatsApp
                  </a>
                  <a
                    href={`mailto:${selectedLead.email}`}
                    className="btn-secondary flex-1 flex items-center justify-center gap-2"
                    data-testid="lead-email-btn"
                  >
                    <Mail className="w-4 h-4" />
                    Email
                  </a>
                  <a
                    href={`tel:${selectedLead.phone}`}
                    className="btn-secondary flex items-center justify-center gap-2"
                    data-testid="lead-call-btn"
                  >
                    <Phone className="w-4 h-4" />
                  </a>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-fp-bg rounded-sm">
                    <label className="text-fp-textMuted text-xs">Email</label>
                    <p className="text-white">{selectedLead.email}</p>
                  </div>
                  <div className="p-4 bg-fp-bg rounded-sm">
                    <label className="text-fp-textMuted text-xs">Phone</label>
                    <p className="text-white">{selectedLead.phone}</p>
                  </div>
                  <div className="p-4 bg-fp-bg rounded-sm">
                    <label className="text-fp-textMuted text-xs">Budget</label>
                    <p className="text-white capitalize">{selectedLead.budget_range?.replace('_', ' ') || 'Not specified'}</p>
                  </div>
                  <div className="p-4 bg-fp-bg rounded-sm">
                    <label className="text-fp-textMuted text-xs">Timeline</label>
                    <p className="text-white capitalize">{selectedLead.timeline?.replace('_', ' ') || 'Not specified'}</p>
                  </div>
                  <div className="p-4 bg-fp-bg rounded-sm">
                    <label className="text-fp-textMuted text-xs">City</label>
                    <p className="text-white">{selectedLead.city || 'Not specified'}</p>
                  </div>
                  <div className="p-4 bg-fp-bg rounded-sm">
                    <label className="text-fp-textMuted text-xs">Industry</label>
                    <p className="text-white">{selectedLead.industry || 'Not specified'}</p>
                  </div>
                </div>

                {/* Services */}
                {selectedLead.services_needed?.length > 0 && (
                  <div>
                    <label className="text-fp-textMuted text-xs block mb-2">Services Needed</label>
                    <div className="flex flex-wrap gap-2">
                      {selectedLead.services_needed.map((service) => (
                        <span
                          key={service}
                          className="px-3 py-1 bg-primary/10 text-primary text-sm rounded"
                        >
                          {service.replace('_', ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Goals */}
                {selectedLead.goals?.length > 0 && (
                  <div>
                    <label className="text-fp-textMuted text-xs block mb-2">Goals</label>
                    <div className="flex flex-wrap gap-2">
                      {selectedLead.goals.map((goal) => (
                        <span
                          key={goal}
                          className="px-3 py-1 bg-white/5 text-fp-textMuted text-sm rounded"
                        >
                          {goal.replace('_', ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Message */}
                {selectedLead.message && (
                  <div>
                    <label className="text-fp-textMuted text-xs block mb-2">Message</label>
                    <p className="p-4 bg-fp-bg rounded-sm text-white text-sm">
                      {selectedLead.message}
                    </p>
                  </div>
                )}

                {/* Status Change */}
                <div>
                  <label className="text-fp-textMuted text-xs block mb-2">Move to Stage</label>
                  <div className="flex flex-wrap gap-2">
                    {stages.map((stage) => (
                      <button
                        key={stage.id}
                        onClick={() => {
                          updateLeadStatus(selectedLead.id, stage.id);
                          setSelectedLead({ ...selectedLead, status: stage.id });
                        }}
                        className={`px-3 py-2 rounded-sm text-sm transition-colors ${
                          selectedLead.status === stage.id
                            ? 'bg-primary text-black'
                            : 'bg-white/5 text-fp-textMuted hover:bg-white/10'
                        }`}
                        data-testid={`lead-stage-${stage.id}`}
                      >
                        {stage.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Convert Button */}
                {selectedLead.status !== 'won' && selectedLead.status !== 'lost' && (
                  <Button
                    onClick={() => convertToClient(selectedLead.id)}
                    className="w-full btn-primary"
                    data-testid="convert-to-client-btn"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Convert to Client
                  </Button>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default LeadsPage;
